// ---------------------------------------------------------------------------
// Unified API helpers for /api/issues. All requests go through the canonical
// `api` client in `@/lib/api` (auth injection, 401 logout/redirect, error
// reporting — all centralized there).
// ---------------------------------------------------------------------------

import type { ParsedTable } from '@/components/database/types';
import { api, apiErrorMessage } from '@/lib/api';
import { buildPath, DASHBOARD_PATHS } from '@/lib/paths';
import type { Issue, IssueDetail, IssueFacets, IssuePriority, IssueStatus } from './types';

/** Append `?token=<jwt>` to a URL so `<img>`/`<video>` tags can load
 *  auth-protected resources without fetch(). */
export function authedUrl(url: string): string {
  const token = localStorage.getItem('auth_token');
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

// ---------------------------------------------------------------------------
// Issues list + detail
// ---------------------------------------------------------------------------

export interface ListIssuesParams {
  type?: string; // comma-separated: "crash,exception,anr"
  statusId?: string;
  statusIds?: string; // comma-joined specific status ids (from `status:` tokens)
  statusCategory?: string; // backlog|unstarted|started|completed|canceled
  /** Comma-joined status categories to hide (e.g. "completed" to drop resolved).
   *  The Issues "All" tab sets this so resolved issues are hidden by default. */
  excludeStatusCategory?: string;
  excludeTriageInbox?: boolean;
  triageInboxOnly?: boolean;
  /** Default triage view hides issues that have any status (they've moved to Tasks). */
  excludeWithStatus?: boolean;
  /** Default triage view hides currently-snoozed issues. */
  excludeSnoozed?: boolean;
  /** `is:snoozed` filter — show only snoozed rows. */
  snoozedOnly?: boolean;
  priorityId?: string;
  assignee?: string;
  // ── Sentry-style facet filters (comma-joined include + `*Not` exclude) ──
  platform?: string;
  platformNot?: string;
  build?: string;
  buildNot?: string;
  device?: string;
  deviceNot?: string;
  osVersion?: string;
  osVersionNot?: string;
  /** Install channel: store | sideload | unknown (drives the `installer:` token). */
  installer?: string;
  installerNot?: string;
  /** iOS sandbox receipt: "true" | "false" (drives the `sandbox:` token). Compose
   *  with installer:store → sandbox:true = TestFlight, sandbox:false = production. */
  sandboxReceipt?: string;
  sandboxReceiptNot?: string;
  /** Distribution channel: store | alpha | dev (drives the `channel:` token).
   *  Resolved server-side via buildChannelService (AppFigures publication). */
  channel?: string;
  channelNot?: string;
  reporterRole?: string;
  reporterRoleNot?: string;
  /** Runtime-environment class filter (CSV of emulated|tampered|genuine).
   *  `environmentNot` excludes (default hides tampered,emulated). */
  environment?: string;
  environmentNot?: string;
  /** Issues filed by this reporter email (drives the `user:` search token). */
  reporterEmail?: string;
  label?: string;
  labelNot?: string;
  /** Test Reports / Bug Reports tab split on TEST_REPORT_LABEL. */
  testReports?: 'only' | 'exclude';
  /** `test:` — comma-joined test session ids or prefixes. */
  testSession?: string;
  /** `source:testing` — only issues an automated test raised. */
  fromTests?: boolean;
  hasVideo?: boolean;
  hasScreenshot?: boolean;
  hasLogs?: boolean;
  regression?: boolean;
  gathering?: boolean;
  /** is:latest — narrow to the newest build in the query's channel scope. */
  latest?: boolean;
  // ── Numeric / date "between values" ranges ──
  eventCountMin?: number;
  eventCountMax?: number;
  deviceCountMin?: number;
  deviceCountMax?: number;
  firstSeenAfter?: string;
  firstSeenBefore?: string;
  lastSeenAfter?: string;
  lastSeenBefore?: string;
  timeRangeMs?: number;
  page?: number;
  pageSize?: number;
  sortBy?: string; // last_seen|first_seen|event_count|user_count|title
  sortDir?: 'asc' | 'desc';
  q?: string;
}

/** A test session a `test:` filter resolved to. */
export interface TestSessionRef {
  id: string;
  title: string;
  created_at: string;
}

/** The test session that raised an issue, and the check it was on (seconds into its recording). */
export interface IssueTestOrigin {
  session_id: string;
  session_title: string;
  check: string | null;
  at_sec: number;
  ts: string;
}

export async function listIssues(params: ListIssuesParams = {}): Promise<{
  items: Issue[];
  total: number;
  page: number;
  pageSize: number;
  testSessions?: TestSessionRef[];
}> {
  const p = new URLSearchParams();
  if (params.type) p.set('type', params.type);
  if (params.statusId) p.set('statusId', params.statusId);
  if (params.statusIds) p.set('statusIds', params.statusIds);
  if (params.statusCategory) p.set('statusCategory', params.statusCategory);
  if (params.excludeStatusCategory) p.set('excludeStatusCategory', params.excludeStatusCategory);
  if (params.excludeTriageInbox) p.set('excludeTriageInbox', '1');
  if (params.triageInboxOnly) p.set('triageInboxOnly', '1');
  if (params.excludeWithStatus) p.set('excludeWithStatus', '1');
  if (params.excludeSnoozed) p.set('excludeSnoozed', '1');
  if (params.snoozedOnly) p.set('snoozedOnly', '1');
  if (params.priorityId) p.set('priorityId', params.priorityId);
  if (params.assignee) p.set('assignee', params.assignee);
  if (params.platform) p.set('platform', params.platform);
  if (params.platformNot) p.set('platformNot', params.platformNot);
  if (params.build) p.set('build', params.build);
  if (params.buildNot) p.set('buildNot', params.buildNot);
  if (params.device) p.set('device', params.device);
  if (params.deviceNot) p.set('deviceNot', params.deviceNot);
  if (params.osVersion) p.set('osVersion', params.osVersion);
  if (params.osVersionNot) p.set('osVersionNot', params.osVersionNot);
  if (params.installer) p.set('installer', params.installer);
  if (params.installerNot) p.set('installerNot', params.installerNot);
  if (params.sandboxReceipt) p.set('sandboxReceipt', params.sandboxReceipt);
  if (params.sandboxReceiptNot) p.set('sandboxReceiptNot', params.sandboxReceiptNot);
  if (params.channel) p.set('channel', params.channel);
  if (params.channelNot) p.set('channelNot', params.channelNot);
  if (params.reporterRole) p.set('reporterRole', params.reporterRole);
  if (params.reporterRoleNot) p.set('reporterRoleNot', params.reporterRoleNot);
  if (params.environment) p.set('environment', params.environment);
  if (params.environmentNot) p.set('environmentNot', params.environmentNot);
  if (params.reporterEmail) p.set('reporterEmail', params.reporterEmail);
  if (params.label) p.set('label', params.label);
  if (params.labelNot) p.set('labelNot', params.labelNot);
  if (params.testReports) p.set('testReports', params.testReports);
  if (params.testSession) p.set('testSession', params.testSession);
  if (params.fromTests) p.set('fromTests', '1');
  if (params.hasVideo) p.set('hasVideo', '1');
  if (params.hasScreenshot) p.set('hasScreenshot', '1');
  if (params.hasLogs) p.set('hasLogs', '1');
  if (params.regression) p.set('regression', '1');
  if (params.gathering) p.set('gathering', '1');
  if (params.latest) p.set('latest', '1');
  if (params.eventCountMin != null) p.set('eventCountMin', String(params.eventCountMin));
  if (params.eventCountMax != null) p.set('eventCountMax', String(params.eventCountMax));
  if (params.deviceCountMin != null) p.set('deviceCountMin', String(params.deviceCountMin));
  if (params.deviceCountMax != null) p.set('deviceCountMax', String(params.deviceCountMax));
  if (params.firstSeenAfter) p.set('firstSeenAfter', params.firstSeenAfter);
  if (params.firstSeenBefore) p.set('firstSeenBefore', params.firstSeenBefore);
  if (params.lastSeenAfter) p.set('lastSeenAfter', params.lastSeenAfter);
  if (params.lastSeenBefore) p.set('lastSeenBefore', params.lastSeenBefore);
  if (params.timeRangeMs) p.set('timeRangeMs', String(params.timeRangeMs));
  if (params.page) p.set('page', String(params.page));
  if (params.pageSize) p.set('pageSize', String(params.pageSize));
  if (params.sortBy) p.set('sortBy', params.sortBy);
  if (params.sortDir) p.set('sortDir', params.sortDir);
  if (params.q) p.set('q', params.q);

  return api(`${DASHBOARD_PATHS.issues}?${p}`);
}

export async function issueTestOrigins(ids: string[]): Promise<Record<string, IssueTestOrigin>> {
  if (ids.length === 0) return {};
  const p = new URLSearchParams({ ids: ids.join(',') });
  const r = await api<{ origins: Record<string, IssueTestOrigin> }>(
    `${DASHBOARD_PATHS.issuesTestOrigins}?${p}`,
  );
  return r.origins;
}

export async function countIssues(
  params: Pick<
    ListIssuesParams,
    | 'excludeTriageInbox'
    | 'excludeWithStatus'
    | 'excludeSnoozed'
    | 'excludeStatusCategory'
    | 'timeRangeMs'
    | 'environmentNot'
  > = {},
): Promise<{ byType: Record<string, number> }> {
  const p = new URLSearchParams();
  if (params.excludeTriageInbox) p.set('excludeTriageInbox', '1');
  if (params.excludeWithStatus) p.set('excludeWithStatus', '1');
  if (params.excludeSnoozed) p.set('excludeSnoozed', '1');
  if (params.excludeStatusCategory) p.set('excludeStatusCategory', params.excludeStatusCategory);
  if (params.environmentNot) p.set('environmentNot', params.environmentNot);
  if (params.timeRangeMs) p.set('timeRangeMs', String(params.timeRangeMs));

  return api(`${DASHBOARD_PATHS.issuesCounts}?${p}`);
}

export async function getIssue(id: string): Promise<IssueDetail> {
  return api(buildPath(DASHBOARD_PATHS.issuesById, { id }));
}

/** Distinct filter values for the search-bar value dropdowns. Project scope is
 *  resolved server-side from the X-Project-Id header; passing projectId keeps
 *  the cache key explicit. */
export async function getIssueFacets(projectId?: string): Promise<IssueFacets> {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return api(`${DASHBOARD_PATHS.issuesFacets}${qs}`);
}

/** Another issue — automatic incident or user report — that occurred around the
 *  same time as this one (same play session, or same device in a tight window). */
export interface RelatedIssue {
  issueId: string;
  type: string; // crash | exception | anr | gpu_hang | bug_report | feedback_item
  title: string;
  exceptionType: string | null;
  blameFunction: string | null;
  blameLocation: string | null;
  occurrenceId: string;
  ts: string; // ISO of the matched occurrence
  deltaMs: number; // signed: occurrence − this issue (negative = before this issue)
  matchedBy: 'session' | 'device';
  statusCategory: string | null;
  screenshotUrl: string | null;
}

/** Issues (crashes, exceptions, ANRs, reports, feedback) that happened around
 *  the same time as this one. Empty for non-correlating types (e.g. tasks). */
export async function listRelatedIssues(id: string): Promise<RelatedIssue[]> {
  const data = await api<{ items?: RelatedIssue[] }>(
    buildPath(DASHBOARD_PATHS.issuesByIdRelated, { id }),
  );
  return data.items ?? [];
}

// ---------------------------------------------------------------------------
// Linked session correlation
// ---------------------------------------------------------------------------

export interface LinkedSessionSummary {
  linkedSessionId: string;
  /** The game match key (the human label). Several runs share it; the start
   *  time tells them apart. Falls back to the id for legacy rows. */
  linkKey: string;
  tags: Record<string, string>;
  platforms: string[];
  deviceCount: number;
  playerCount: number;
  eventCount: number;
  firstSeen: number | null;
  lastSeen: number | null;
  /** Any participant device currently holds a report tunnel (live) — overlaid by
   *  the route, the same liveness other logs show. */
  online?: boolean;
}

export interface LinkedSessionParticipant {
  deviceId: string;
  deviceName: string | null;
  platform: string | null;
  userId: string | null;
  firstSeen: number;
  lastSeen: number;
  eventCount: number;
  issueCount: number;
  /** This client's own match view (role=host, team, name…). */
  tags: Record<string, string>;
}

export interface LinkedSessionCohortIssue {
  issueId: string;
  type: string;
  title: string;
  deviceId: string | null;
  deviceName: string | null;
  platform: string | null;
  ts: number;
}

export interface LinkedSessionCohort {
  session: LinkedSessionSummary | null;
  participants: LinkedSessionParticipant[];
  siblingIssues: LinkedSessionCohortIssue[];
}

/** The linked match this issue occurred in (if any): the other clients in
 *  the match + their sibling issues. `session` is null when the issue never
 *  happened inside a Bugpunch.JoinLinkedSession. */
export async function getLinkedSessionCohort(id: string): Promise<LinkedSessionCohort> {
  return api<LinkedSessionCohort>(buildPath(DASHBOARD_PATHS.issuesByIdLinkedSessionCohort, { id }));
}

// ---------------------------------------------------------------------------
// Sample databases — a crash sample's raw game database attachments (Siaqodb
// `.sqo` dirs, Odin blobs, custom formats) parsed server-side with the game's
// own uploaded assemblies + database plugins.
// ---------------------------------------------------------------------------

export type SampleDatabasesStatus = 'ok' | 'no-attachments' | 'host-unavailable' | 'error';

export interface SampleDatabasePlugin {
  id: string;
  displayName: string;
  extensions: string[];
  available: boolean;
  /** One store spans a directory of files (Siaqodb) — the entry path is one
   *  file inside it, so the store is labelled by its parent directory. */
  directoryBased?: boolean;
  assembly?: string;
  type?: string;
}

export interface SampleDatabaseEntry {
  path: string;
  providerId: string;
  durationMs?: number;
  result: { ok: boolean; error?: string; tables?: ParsedTable[] };
}

export interface SampleDatabasesResponse {
  status: SampleDatabasesStatus;
  error?: string;
  cached: boolean;
  /** Assembly bundle the parse ran against; `exact: false` means the sample's
   *  own build had no upload and the nearest build's assemblies were used.
   *  `null` = no upload for this game at all: the host's built-in providers
   *  ran alone (fixed formats like Siaqodb need nothing from the game). */
  bundle: {
    buildHash: string;
    version: string | null;
    platform: string | null;
    exact: boolean;
  } | null;
  builtinOnly: boolean;
  plugins: SampleDatabasePlugin[];
  entries: SampleDatabaseEntry[];
  /** Plugin-host output (including the game plugin's Debug.Log lines). */
  log: string[];
  durationMs?: number;
}

/** The first call for a sample runs a sandboxed .NET job and can take ~90 s;
 *  the result is cached after that. `refresh` bypasses the cache. */
export async function fetchSampleDatabases(
  issueId: string,
  sampleId: string,
  refresh = false,
): Promise<SampleDatabasesResponse> {
  const path = buildPath(DASHBOARD_PATHS.issuesByIdSamplesBySampleIdDatabases, {
    id: issueId,
    sampleId,
  });
  const r = await api<SampleDatabasesResponse>(refresh ? `${path}?refresh=1` : path);
  return { ...r, plugins: r.plugins ?? [], entries: r.entries ?? [], log: r.log ?? [] };
}

/** Manually file an issue/ticket from the dashboard. Server seeds it into the
 *  project's default status with a `manualCreate` tag. `type` excludes 'task'
 *  (tasks are created from the Tasks board, not here). */
export async function createManualIssue(input: {
  type: 'crash' | 'exception' | 'anr' | 'bug_report' | 'feedback_item';
  title: string;
  description?: string;
}): Promise<IssueDetail> {
  try {
    return await api(DASHBOARD_PATHS.issues, { method: 'POST', body: input });
  } catch (e) {
    throw new Error(apiErrorMessage(e));
  }
}

export async function patchIssue(
  id: string,
  patch: {
    /** `null` clears the status back to fresh-intake — used by Triage's Undo. */
    statusId?: string | null;
    priorityId?: string;
    title?: string;
    description?: string;
    labels?: string[];
    flags?: string[];
  },
): Promise<IssueDetail> {
  return api(buildPath(DASHBOARD_PATHS.issuesById, { id }), { method: 'PATCH', body: patch });
}

// ---------------------------------------------------------------------------
// Watchers — "loop in people familiar with this" during triage. Not assignment:
// watchers are notified on activity but there's no owner. Backed by the shared
// task-subscriber list (issues + tasks share the table).
// ---------------------------------------------------------------------------

export async function addIssueWatchers(id: string, userIds: string[]): Promise<string[]> {
  const data = await api<{ userIds?: string[] }>(
    buildPath(DASHBOARD_PATHS.issuesByIdWatchers, { id }),
    {
      method: 'POST',
      body: { userIds },
    },
  );
  return data.userIds ?? [];
}

// ---------------------------------------------------------------------------
// Snooze (Sentry-style "archive until")
// ---------------------------------------------------------------------------

export type SnoozeConditionType = 'time' | 'events' | 'users' | 'release';

export interface SnoozeInput {
  conditionType: SnoozeConditionType;
  /** time:    ISO timestamp string to wake at.
   *  events:  number of additional events from now.
   *  users:   number of additional users from now.
   *  release: ignored (server captures latest event's build_version). */
  value: string | number;
  hardCeilingUntil?: string;
  reason?: string;
}

export async function snoozeIssue(id: string, input: SnoozeInput): Promise<IssueDetail> {
  try {
    return await api(buildPath(DASHBOARD_PATHS.issuesByIdSnooze, { id }), {
      method: 'POST',
      body: input,
    });
  } catch (e) {
    throw new Error(apiErrorMessage(e));
  }
}

// ---------------------------------------------------------------------------
// Bulk patch — issue ids + a single patch applied to all
// ---------------------------------------------------------------------------

export interface BulkPatchInput {
  statusId?: string;
  /** Replace each issue's labels with this exact list. */
  labels?: string[];
  flags?: string[];
  /** Additive: append to each issue's existing array (no-op when present). */
  addLabel?: string;
  addFlag?: string;
  /** Subtractive: drop from each issue's existing array. */
  removeLabel?: string;
  removeFlag?: string;
}

export async function bulkPatchIssues(
  ids: string[],
  patch: BulkPatchInput,
): Promise<{ updated: number; failed: number }> {
  return api(DASHBOARD_PATHS.issuesBulkPatch, { method: 'POST', body: { ids, patch } });
}

export async function deleteIssue(id: string): Promise<void> {
  await api(buildPath(DASHBOARD_PATHS.issuesById, { id }), { method: 'DELETE' });
}

export async function bulkDeleteIssues(
  ids: string[],
): Promise<{ deleted: number; failed: number }> {
  return api(DASHBOARD_PATHS.issuesBulkDelete, { method: 'POST', body: { ids } });
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export async function resolveIssue(
  id: string,
  opts: {
    fixedInVersion?: string;
    conditionType?: 'build' | 'events' | 'users' | 'samples';
    conditionValue?: string;
  } = {},
): Promise<IssueDetail> {
  try {
    return await api(buildPath(DASHBOARD_PATHS.issuesByIdResolve, { id }), {
      method: 'POST',
      body: opts,
    });
  } catch (e) {
    throw new Error(apiErrorMessage(e));
  }
}

export async function ignoreIssue(id: string): Promise<IssueDetail> {
  return api(buildPath(DASHBOARD_PATHS.issuesByIdIgnore, { id }), { method: 'POST', body: {} });
}

export async function reopenIssue(id: string, reason?: string): Promise<IssueDetail> {
  return api(buildPath(DASHBOARD_PATHS.issuesByIdReopen, { id }), {
    method: 'POST',
    body: { reason },
  });
}

/** Open (or roll forward) the issue's 24h public share window and return the
 *  public link. Same endpoint the detail panel's Share button hits — Claude's
 *  web fetch on this URL auto-redirects to the `.md` agent brief, while a
 *  logged-in teammate is bounced into the full dashboard. Shared by the detail
 *  header and the list-row share icon so the mint logic lives in one place.
 *  `sampleId` pins the sample the sharer is viewing — the public surfaces
 *  (markdown, JSON, logs, storyboard, images, video) anchor to it instead of
 *  the newest sample. Omit it (list-row share) to anchor to newest. */
export async function shareIssue(
  id: string,
  sampleId?: string,
): Promise<{ Url: string; ExpiresAt: string }> {
  return api(buildPath(DASHBOARD_PATHS.issuesByIdShare, { id }), {
    method: 'POST',
    body: sampleId ? { sampleId } : {},
  });
}

export async function mergeIssues(ids: string[]): Promise<unknown> {
  return api(DASHBOARD_PATHS.issuesMerge, { method: 'POST', body: { ids } });
}

export async function unmergeIssue(id: string): Promise<unknown> {
  return api(buildPath(DASHBOARD_PATHS.issuesByIdUnmerge, { id }), { method: 'POST', body: {} });
}

// ---------------------------------------------------------------------------
// Status pool
// ---------------------------------------------------------------------------

export async function listStatuses(projectId: string): Promise<IssueStatus[]> {
  const data = await api<IssueStatus[] | { items?: IssueStatus[] }>(
    `${DASHBOARD_PATHS.issueStatuses}?projectId=${encodeURIComponent(projectId)}`,
  );
  return Array.isArray(data) ? data : (data.items ?? []);
}

export async function createStatus(
  projectId: string,
  payload: {
    name: string;
    color: string;
    category: string;
    isRegressionTarget?: boolean;
    isResolveTarget?: boolean;
  },
): Promise<IssueStatus> {
  return api(DASHBOARD_PATHS.issueStatuses, { method: 'POST', body: { projectId, ...payload } });
}

export async function updateStatus(
  id: string,
  payload: Partial<{
    name: string;
    color: string;
    category: string;
    isRegressionTarget: boolean;
    isResolveTarget: boolean;
  }>,
): Promise<IssueStatus> {
  return api(buildPath(DASHBOARD_PATHS.issueStatusesById, { id }), {
    method: 'PATCH',
    body: payload,
  });
}

export async function deleteStatus(id: string): Promise<void> {
  await api(buildPath(DASHBOARD_PATHS.issueStatusesById, { id }), { method: 'DELETE' });
}

// Priority pool removed. Urgency lives in the status pool now
// (Urgent / Soon / Eventually). Stubs kept so any straggler call site
// fails loudly instead of returning empty.
export async function listPriorities(_projectId: string): Promise<IssuePriority[]> {
  return [];
}
export async function createPriority(
  _projectId: string,
  _payload: { name: string; color: string },
): Promise<IssuePriority> {
  return Promise.reject(new Error('createPriority: removed; status encodes urgency'));
}
export async function updatePriority(
  _id: string,
  _payload: Partial<{ name: string; color: string }>,
): Promise<IssuePriority> {
  return Promise.reject(new Error('updatePriority: removed'));
}
export async function deletePriority(_id: string): Promise<void> {
  return Promise.reject(new Error('deletePriority: removed'));
}

// ---------------------------------------------------------------------------
// Agent shares — hand the current filtered list to a coding agent
// ---------------------------------------------------------------------------

export interface AgentShareCreated {
  id: string;
  url: string;
  expiresAt: string;
  issueCount: number;
  /** Everything the filter matched; issueCount is capped below it. */
  matchedCount: number;
  prompt: string;
}

/** Brief questions answered at share time (mirror of the server's AgentShareOptions); 'ask' leaves them to the agent. */
export interface AgentShareOptions {
  commit: 'ask' | 'push' | 'local' | 'merge' | 'direct' | 'none';
  signOff: 'ask' | 'review' | 'resolve' | 'comment';
}

export interface AgentShareSummary {
  id: string;
  label: string;
  /** null = hand-picked issues. */
  query: string | null;
  issueCount: number;
  options: AgentShareOptions;
  expiresAt: string;
  createdAt: string;
}

/** Shares `issueIds` when given, else everything `query` matches. */
export async function createAgentShare(
  projectId: string,
  query: string,
  issueIds: string[] = [],
  options?: AgentShareOptions,
): Promise<AgentShareCreated> {
  try {
    return await api(buildPath(DASHBOARD_PATHS.projectsByProjectIdAgentShares, { projectId }), {
      method: 'POST',
      body: { ...(issueIds.length ? { issueIds } : { query }), options },
    });
  } catch (e) {
    throw new Error(apiErrorMessage(e));
  }
}

export async function listAgentShares(projectId: string): Promise<AgentShareSummary[]> {
  const r = await api<{ items: AgentShareSummary[] }>(
    buildPath(DASHBOARD_PATHS.projectsByProjectIdAgentShares, { projectId }),
  );
  return r.items;
}

/** Shares are personal api_keys rows, so revoking is the ordinary key delete. */
export async function revokeAgentShare(keyId: string): Promise<void> {
  await api(buildPath(DASHBOARD_PATHS.usersMeApiKeysByKeyId, { keyId }), { method: 'DELETE' });
}
